import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import LayersOutlined from "@mui/icons-material/LayersOutlined";
import EqualizerOutlined from "@mui/icons-material/EqualizerOutlined";
import DnsOutlined from "@mui/icons-material/DnsOutlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import HelpOutline from "@mui/icons-material/HelpOutline";
import { ShieldOutlined, MenuBookOutlined } from "@mui/icons-material";

const withFilteredProps = (WrappedComponent) => {
  return ({ isActive, ...props }) => {
    return <WrappedComponent {...props} />;
  };
};

const FilteredNavLink = withFilteredProps(NavLink);

// Shared nav-entry styling — reused by the routed link and the button variant
// (HELP, which summons the palette rather than navigating).
const navEntryStyle = (theme, isActive) => ({
  color: theme.palette.text.navigation,
  fontSize: "1.2rem",
  padding: theme.spacing(1),
  textDecoration: "none",
  display: "flex",
  alignItems: "center",
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.3s ease",
  fontFamily: theme.typography.subtitle1.fontFamily,
  fontWeight: theme.typography.subtitle1.fontWeight,

  "&::before": {
    content: '""',
    width: "40px",
    height: "40px",
    backgroundColor: theme.palette.primary.main,
    position: "absolute",
    left: "-45px",
    top: "50%",
    transform: "translateY(-50%)",
    borderRadius: "15px",
    opacity: isActive ? "1" : "0",
    transition: "all 0.3s ease",
  },

  "&:hover::before": {
    opacity: 1,
    left: "-42px",
  },

  // Active state styling
  ...(isActive && {
    color: theme.palette.primary.main,
  }),
});

const StyledNavLink = styled(FilteredNavLink)(({ theme, isActive }) =>
  navEntryStyle(theme, isActive),
);

// Button variant: identical treatment, but a plain <button> for the HELP
// entry which opens the command palette instead of routing.
const StyledNavButton = styled("button")(({ theme, isActive }) => ({
  ...navEntryStyle(theme, isActive),
  width: "100%",
  background: "none",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
}));

const NavLinkText = styled("span")(({ theme, collapsed }) => ({
  opacity: collapsed ? "0" : "1",
  visibility: collapsed ? "hidden" : "visible",
  transition: "opacity 0.3s ease, visibility 0s ease",
  width: collapsed ? "0px" : "auto",
  whiteSpace: "nowrap",
  overflow: "hidden",
}));

const StyledSpacerDiv = styled("div")(({ theme }) => ({
  display: "inline-block",
  marginLeft: theme.spacing(1),
}));

const iconFor = (children) => (
  <>
    {children === "FOUNDATION" && <HomeOutlined fontSize="large" />}
    {children === "UPKEEP" && <LayersOutlined fontSize="large" />}
    {children === "REPORTS" && <EqualizerOutlined fontSize="large" />}
    {children === "HISTORY" && <DnsOutlined fontSize="large" />}
    {children === "CONTACTS" && <PeopleAltOutlined fontSize="large" />}
    {children === "ADMIN" && <ShieldOutlined fontSize="large" />}
    {children === "BOOKKEEPING" && <MenuBookOutlined fontSize="large" />}
    {children === "SETTINGS" && <SettingsOutlinedIcon fontSize="large" />}
    {children === "HELP" && <HelpOutline fontSize="large" />}
  </>
);

const ParentNavLink = ({ to, collapsed, children, onClick, testId }) => {
  const location = useLocation();
  const parentLocation = to ? to.split("/")[1] : "";
  const [isActive, setIsActive] = useState(
    !!parentLocation && location.pathname.includes(parentLocation),
  );

  useEffect(() => {
    setIsActive(
      !!parentLocation && location.pathname.split("/")[1].includes(parentLocation),
    );
  }, [location, parentLocation]);

  // Button variant (no route) — summons the palette instead of navigating.
  if (onClick) {
    return (
      <StyledNavButton
        type="button"
        isActive={false}
        onClick={onClick}
        data-testid={testId}
        aria-label={typeof children === "string" ? children : undefined}>
        {iconFor(children)}
        <StyledSpacerDiv />
        <NavLinkText collapsed={collapsed}>{children}</NavLinkText>
      </StyledNavButton>
    );
  }

  return (
    <StyledNavLink isActive={isActive} to={to} data-testid={testId}>
      {iconFor(children)}
      <StyledSpacerDiv />
      <NavLinkText collapsed={collapsed}>{children}</NavLinkText>
    </StyledNavLink>
  );
};

export default ParentNavLink;
